import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Heart, Phone } from "lucide-react";
import { BUSINESS_INFO } from "@/lib/constants";
import { trackNavigation, trackSMSClick, trackCTAClick } from "@/lib/analytics";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Pricing", path: "/pricing" },
    { name: "Gallery", path: "/gallery" },
    { name: "Locations", path: "/locations" },
    { name: "Breeds", path: "/breeds/dogs" },
    { name: "About", path: "/about" },
    { name: "Blog", path: "/blog" },
    { name: "FAQ", path: "/faq" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <Heart className="h-8 w-8 text-primary" fill="currentColor" />
              <span className="text-xl font-bold text-gradient-hero">
                Groomie Roomie
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  data-gtm-id={`nav-desktop-${item.name.toLowerCase()}-link`}
                  onClick={() => trackNavigation(item.name, 'desktop_nav', item.path)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? "text-primary border-b-2 border-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Button variant="outline" size="sm" asChild>
                <a 
                  href={BUSINESS_INFO.phone.sms} 
                  className="flex items-center gap-2" 
                  aria-label={`Text Groomie Roomie at ${BUSINESS_INFO.phone.digits}`}
                  data-gtm-id="nav-desktop-sms-click"
                  onClick={() => trackSMSClick('desktop_nav')}
                >
                  <Phone className="h-4 w-4" />
                  {BUSINESS_INFO.phone.display}
                </a>
              </Button>
              <Button variant="hero" size="sm" asChild>
                <Link to="/booking" data-gtm-id="nav-desktop-book-now-click" onClick={() => trackCTAClick('Book Now', 'desktop_nav', '/booking')}>
                  Book Now
                </Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-gtm-id="nav-mobile-menu-toggle"
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-primary"
              aria-label={isOpen ? "Close mobile menu" : "Open mobile menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden border-t bg-background/95 backdrop-blur-md">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  data-gtm-id={`nav-mobile-${item.name.toLowerCase()}-link`}
                  onClick={() => {
                    trackNavigation(item.name, 'mobile_nav', item.path);
                    setIsOpen(false);
                  }}
                  className={`block py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Button variant="outline" size="sm" className="w-full mt-4" asChild>
                <a 
                  href={BUSINESS_INFO.phone.sms} 
                  className="flex items-center justify-center gap-2" 
                  aria-label={`Text Groomie Roomie at ${BUSINESS_INFO.phone.digits}`}
                  data-gtm-id="nav-mobile-sms-click"
                  onClick={() => trackSMSClick('mobile_nav')}
                >
                  <Phone className="h-5 w-5" />
                  {BUSINESS_INFO.phone.display}
                </a>
              </Button>
              <Button variant="hero" size="sm" className="w-full mt-2" asChild>
                <Link 
                  to="/booking" 
                  data-gtm-id="nav-mobile-book-now-click"
                  onClick={() => {
                    trackCTAClick('Book Now', 'mobile_nav', '/booking');
                    setIsOpen(false);
                  }}
                >
                  Book Now
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;